/**
 * URL Unlocking Page
 * System Settings page for querying and managing URL path bindings
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Loader, Message, Segment } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { useSearchParams } from 'react-router-dom';
import {
  PageUrlElementModel,
  UrlTargetType,
  FormMode,
  ExtractedEntityIds
} from './types';
import {
  fetchPublicPageData,
  fetchPageUrlByScope,
  deletePageUrl,
  extractEntityIds,
  buildUrlScopeKey
} from './api';
import { i18nKeys } from './constants';
import UrlUnlockingForm, {
  UrlUnlockingFormInitialValues,
  UrlUnlockingSearchArgs
} from './components/UrlUnlockingForm';
import PageUrlResultsList from './components/PageUrlResultsList';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import styles from './UrlUnlocking.module.css';

const QS = {
  MODE: 'mode',
  TARGET: 'target',
  URL: 'url',
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary'
} as const;

const isFormMode = (v: string | null): v is FormMode => v === 'easy' || v === 'advanced';
const isTargetType = (v: string | null): v is UrlTargetType =>
  v === 'primary' || v === 'secondary' || v === 'tertiary';

const UrlUnlocking: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Snapshot URL params on first render so the form only hydrates once.
  const initialFormValuesRef = useRef<UrlUnlockingFormInitialValues>({
    mode: isFormMode(searchParams.get(QS.MODE)) ? (searchParams.get(QS.MODE) as FormMode) : undefined,
    targetType: isTargetType(searchParams.get(QS.TARGET))
      ? (searchParams.get(QS.TARGET) as UrlTargetType)
      : undefined,
    fullUrl: searchParams.get(QS.URL) ?? undefined,
    primaryPath: searchParams.get(QS.PRIMARY) ?? undefined,
    secondaryPath: searchParams.get(QS.SECONDARY) ?? undefined,
    tertiaryPath: searchParams.get(QS.TERTIARY) ?? undefined
  });

  // Query state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<PageUrlElementModel[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<PageUrlElementModel | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Store last search params for re-query after delete
  const [lastSearchParams, setLastSearchParams] = useState<{
    targetType: UrlTargetType;
    primary: string;
    secondary: string;
    tertiary: string;
  } | null>(null);

  /**
   * Perform the search query
   */
  const handleSearch = useCallback(async ({
    targetType,
    primary,
    secondary,
    tertiary,
    mode,
    fullUrl
  }: UrlUnlockingSearchArgs) => {
    // Sync full form state to URL so a refresh restores the page
    const nextParams = new URLSearchParams();
    nextParams.set(QS.MODE, mode);
    nextParams.set(QS.TARGET, targetType);
    if (fullUrl) nextParams.set(QS.URL, fullUrl);
    if (primary) nextParams.set(QS.PRIMARY, primary);
    if (secondary) nextParams.set(QS.SECONDARY, secondary);
    if (tertiary) nextParams.set(QS.TERTIARY, tertiary);
    setSearchParams(nextParams, { replace: true });

    setIsLoading(true);
    setError(null);
    setResults([]);
    setHasSearched(true);
    setLastSearchParams({ targetType, primary, secondary, tertiary });

    try {
      // Step 1: Fetch public page data to get entity IDs
      const publicResponse = await fetchPublicPageData(primary, secondary, tertiary);
      
      if (!publicResponse.success) {
        throw new Error(publicResponse.error || I18n.t(i18nKeys.ERRORS.PUBLIC_FETCH_FAILED));
      }

      const entityIds: ExtractedEntityIds = extractEntityIds(publicResponse.data);

      // Determine which URL path to use for scope key
      let urlPathForScope: string;
      switch (targetType) {
        case 'primary':
          urlPathForScope = primary;
          break;
        case 'secondary':
          urlPathForScope = secondary;
          break;
        case 'tertiary':
          urlPathForScope = tertiary;
          break;
      }

      // Step 2: Build URL scope key
      const urlScope = buildUrlScopeKey(targetType, urlPathForScope, entityIds);
      
      if (!urlScope) {
        throw new Error(I18n.t(i18nKeys.ERRORS.MISSING_REQUIRED_IDS));
      }

      // Step 3: Fetch page URL records
      const pageUrlResponse = await fetchPageUrlByScope(urlScope);
      
      if (!pageUrlResponse.success) {
        throw new Error(pageUrlResponse.error || I18n.t(i18nKeys.ERRORS.PAGE_URL_FETCH_FAILED));
      }

      setResults(pageUrlResponse.data.list || []);
    } catch (err: any) {
      setError(err.message || I18n.t(i18nKeys.ERRORS.PAGE_URL_FETCH_FAILED));
    } finally {
      setIsLoading(false);
    }
  }, [setSearchParams]);

  /**
   * Clear search results
   */
  const handleClear = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setResults([]);
    setError(null);
    setHasSearched(false);
    setLastSearchParams(null);
  }, [setSearchParams]);

  /**
   * On mount: if URL params describe a valid search, kick it off.
   */
  const hasAutoSearchedRef = useRef(false);
  useEffect(() => {
    if (hasAutoSearchedRef.current) return;
    hasAutoSearchedRef.current = true;
    const {
      targetType,
      primaryPath,
      secondaryPath,
      tertiaryPath,
      mode,
      fullUrl
    } = initialFormValuesRef.current;
    if (!targetType || !primaryPath) return;
    if (targetType === 'secondary' && !secondaryPath) return;
    if (targetType === 'tertiary' && (!secondaryPath || !tertiaryPath)) return;
    handleSearch({
      targetType,
      primary: primaryPath,
      secondary: secondaryPath ?? '',
      tertiary: tertiaryPath ?? '',
      mode: mode ?? 'easy',
      fullUrl: fullUrl ?? ''
    });
  }, [handleSearch]);

  /**
   * Open delete confirmation modal
   */
  const handleDeleteClick = useCallback((item: PageUrlElementModel) => {
    setItemToDelete(item);
    setDeleteError(null);
    setDeleteModalOpen(true);
  }, []);

  /**
   * Cancel delete
   */
  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
    setDeleteError(null);
  }, []);

  /**
   * Confirm delete
   */
  const handleDeleteConfirm = useCallback(async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await deletePageUrl(itemToDelete.id);
      
      if (!response.success) {
        throw new Error(response.error || I18n.t(i18nKeys.ERRORS.DELETE_FAILED));
      }

      // Close modal
      setDeleteModalOpen(false);
      setItemToDelete(null);

      // Reload results if we have last search params
      if (lastSearchParams) {
        const modeParam = searchParams.get(QS.MODE);
        const mode: FormMode = isFormMode(modeParam) ? modeParam : 'easy';
        const fullUrl = searchParams.get(QS.URL) ?? '';
        await handleSearch({
          targetType: lastSearchParams.targetType,
          primary: lastSearchParams.primary,
          secondary: lastSearchParams.secondary,
          tertiary: lastSearchParams.tertiary,
          mode,
          fullUrl
        });
      }
    } catch (err: any) {
      setDeleteError(err.message || I18n.t(i18nKeys.ERRORS.DELETE_FAILED));
    } finally {
      setIsDeleting(false);
    }
  }, [itemToDelete, lastSearchParams, handleSearch, searchParams]);

  return (
    <div className={styles.container}>
      <h2><Translate value={i18nKeys.TITLE} /></h2>
      
      {/* Explanation Section */}
      <Segment className={styles.explanationSegment}>
        <h4><Translate value={i18nKeys.EXPLANATION.TITLE} /></h4>
        <div className={styles.explanationContent}>
          <p><Translate value={i18nKeys.EXPLANATION.PRIMARY} /></p>
          <p><Translate value={i18nKeys.EXPLANATION.SECONDARY} /></p>
          <p><Translate value={i18nKeys.EXPLANATION.TERTIARY} /></p>
        </div>
      </Segment>

      {/* Form */}
      <UrlUnlockingForm
        onSearch={handleSearch}
        onClear={handleClear}
        isLoading={isLoading}
        initialValues={initialFormValuesRef.current}
      />

      {/* Error Message */}
      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className={styles.loadingContainer}>
          <Loader active inline="centered" />
        </div>
      )}

      {/* Results */}
      {!isLoading && hasSearched && (
        <PageUrlResultsList
          results={results}
          isLoading={isLoading}
          onDelete={handleDeleteClick}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        item={itemToDelete}
        isDeleting={isDeleting}
        error={deleteError}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
};

export default UrlUnlocking;
